import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useThemeContext } from '../../context/ThemeContext';
import useFetchObjects from '../../api/useFetchObjects';

const Settings = () => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const { mode, toggleTheme } = useThemeContext();
  const token = user?.token;

  // Detect RTL languages (Pashto, Dari)
  const isRtlPage = ['dari', 'ps'].includes(i18n.language);

  // Local state just to illustrate change handlers (non-functional placeholders)
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [textDir, setTextDir] = useState('auto');
  const [darkMode, setDarkMode] = useState(false);
  const [emailNotif, setEmailNotif] = useState(true);
  const [docUpdatesNotif, setDocUpdatesNotif] = useState(true);
  const [workflowNotif, setWorkflowNotif] = useState(true);

  // Fetch existing settings from backend
  const { data: settingsData, isLoading: settingsLoading, refetch } = useFetchObjects(
    'system-settings',
    { format: 'object', noLimit: 'true' },
    token,
    { enabled: !!token }
  );

  useEffect(() => {
    if (settingsData) {
      const shouldBeDark = ['dark', 'true', true, 1, '1'].includes(settingsData.dark_mode);
      setDarkMode(shouldBeDark);
      // Sync context theme if mismatch
      if (shouldBeDark !== (mode === 'dark')) {
        toggleTheme();
      }
      setLanguage(settingsData.language || language);
      if (settingsData.language && settingsData.language !== i18n.language) {
        i18n.changeLanguage(settingsData.language);
        localStorage.setItem('i18nextLng', settingsData.language);
      }
      setTextDir(settingsData.text_direction || textDir);
      setEmailNotif(settingsData.email_notifications !== 'false');
      setDocUpdatesNotif(settingsData.document_updates !== 'false');
      setWorkflowNotif(settingsData.workflow_notifications !== 'false');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsData]);

  const saveSetting = async (key, value) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/system-settings`,
        { key, value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Failed to save setting', key, err);
      throw err;
    }
  };

  const handleSave = async () => {
    if (!token) {
      toast.error(t('settings:authRequired', 'You must be logged in'));
      return;
    }
    try {
      await Promise.all([
        saveSetting('dark_mode', darkMode),
        saveSetting('language', language),
        saveSetting('text_direction', textDir),
        saveSetting('email_notifications', emailNotif),
        saveSetting('document_updates', docUpdatesNotif),
        saveSetting('workflow_notifications', workflowNotif),
      ]);
      // Persist to localStorage for quick access on next login
      localStorage.setItem('theme', darkMode ? 'dark' : 'light');
      localStorage.setItem('i18nextLng', language);
      toast.success(t('settings:saveSuccess', 'Settings saved'));
      refetch();
    } catch (e) {
      toast.error(t('settings:saveError', 'Failed to save'));
    }
  };

  // Common props for list items in RTL
  const listItemDirectionProps = {};

  const textAlignStyle = { textAlign: isRtlPage ? 'right' : 'left' };

  return (
    <Box sx={{ p: 3 }} dir={isRtlPage ? 'rtl' : 'ltr'}>
      <Typography variant="h4" component="h1" gutterBottom align={isRtlPage ? 'right' : 'left'}>
        {t('settings:title', 'Settings')}
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" paragraph align={isRtlPage ? 'right' : 'left'}>
        {t('settings:subtitle', 'Manage your application preferences')}
      </Typography>
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('settings:appearance', 'Appearance')}
          </Typography>
          
          <List>
            <ListItem {...listItemDirectionProps}>
              <ListItemText 
                sx={textAlignStyle}
                primary={t('settings:dark_mode', 'Dark Mode')}
                secondary={t('settings:dark_mode_description', 'Switch between light and dark themes')}
              />
              <Switch edge="end" checked={darkMode} onChange={(e)=>{
                const checked = e.target.checked;
                setDarkMode(checked);
                if (checked !== (mode === 'dark')) {
                  toggleTheme();
                }
              }} />
            </ListItem>
            
            <Divider />
            
            <ListItem {...listItemDirectionProps}>
              <ListItemText 
                sx={textAlignStyle}
                primary={t('settings:language', 'Language')}
                secondary={t('settings:language_description', 'Choose your preferred language')}
              />
              <FormControl sx={{ minWidth: 120 }} size="small">
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="en">{t('common:english', 'English')}</MenuItem>
                  <MenuItem value="ps">{t('common:pashto', 'پښتو')}</MenuItem>
                  <MenuItem value="dari">{t('common:dari', 'دری')}</MenuItem>
                </Select>
              </FormControl>
            </ListItem>
            
            <Divider />
            
           
          </List>
        </CardContent>
      </Card>
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('settings:notifications', 'Notifications')}
          </Typography>
          
          <List>
            <ListItem {...listItemDirectionProps}>
              <ListItemText 
                sx={textAlignStyle}
                primary={t('settings:email_notifications', 'Email Notifications')}
                secondary={t('settings:email_notifications_description', 'Receive notification emails')}
              />
              <Switch edge="end" checked={emailNotif} onChange={(e)=>setEmailNotif(e.target.checked)} />
            </ListItem>
            
            <Divider />
            
            <ListItem {...listItemDirectionProps}>
              <ListItemText 
                sx={textAlignStyle}
                primary={t('settings:document_updates', 'Document Updates')}
                secondary={t('settings:document_updates_description', 'Get notified about document changes')}
              />
              <Switch edge="end" checked={docUpdatesNotif} onChange={(e)=>setDocUpdatesNotif(e.target.checked)} />
            </ListItem>
            
            <Divider />
            
            <ListItem {...listItemDirectionProps}>
              <ListItemText 
                sx={textAlignStyle}
                primary={t('settings:workflow_notifications', 'Workflow Notifications')}
                secondary={t('settings:workflow_notifications_description', 'Get notified about workflow tasks')}
              />
              <Switch edge="end" checked={workflowNotif} onChange={(e)=>setWorkflowNotif(e.target.checked)} />
            </ListItem>
          </List>
        </CardContent>
      </Card>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={!token || settingsLoading}>
          {t('settings:save', 'Save Settings')}
        </Button>
      </Box>
    </Box>
  );
};

export default Settings; 